import { createTheme } from "@mui/material/styles";

const theme = createTheme({
  palette: {
    primary: {
      main: "#1880DF",
      maintwo: "#34A853",
      text: "#707073",
      light: "#deeafc",
      dark: "#013685",
      contrastText: "#ffffff",
      red: "#FF0000",
      green: "#089A08",
      button: "#08089e",
    },
    secondary: {
      main: "#6a1b9a",
      light: "#868E96",
      dark: "#38006b",
      contrastText: "#ffffff",
    },
    background: {
      default: "#fff",
      paper: "#ffffff",
    },
    gradient: {
      primary: "linear-gradient(to right, #1880DF, #1880DF)",
      secondary: "linear-gradient(90deg, #1880DF 0%, #6a1b9a 100%)",
      card: "linear-gradient(135deg, rgba(150, 13, 177, 0.82) 0%, rgba(197, 8, 197, 0.81) 100%)",
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 20,
          transition: "all 0.3s ease",
        },
      },
      variants: [
        {
          props: { variant: "gradient" },
          style: {
            background:
              "linear-gradient(135deg, rgba(150, 13, 177, 0.82) 0%, rgba(197, 8, 197, 0.81) 100%)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            "&:hover": {
              transform: "translateY(-5px)",
              boxShadow: "0 10px 20px rgba(0,0,0,0.2)",
            },
          },
        },
      ],
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 12,
          fontWeight: 400,
          fontFamily: '"Poppins", sans-serif',
        },
      },
      variants: [
        {
          props: { variant: "gradient" },
          style: {
            background: "linear-gradient(90deg, #1880DF, #6a1b9a)",
            color: "#ffffff",
            padding: "12px",
            fontSize: "16px",
            "&:hover": {
              background: "linear-gradient(90deg, #6a1b9a, #1880DF)",
              transform: "translateY(-2px)",
              boxShadow: "0 5px 15px rgba(0,0,0,0.2)",
            },
          },
        },

        {
          props: { variant: "authbutton" },
          style: {
            background: "linear-gradient(90deg, #1880DF 0%, #1880DF 100%)",
            color: "#fff",
            padding: "30px 30px",
            fontSize: "18px",
            borderRadius: "11px",
            fontWeight: 500,
            "&:hover": {
              background: "linear-gradient(90deg, #1880DF 0%, #1880DF 100%)",
              transform: "translateY(-2px)",
              boxShadow: "0 5px 15px rgba(0,0,0,0.2)",
            },
          },
        },
        {
          props: { variant: "socail" },
          style: {
            backgroundColor: "rgba(255, 255, 255, 0.1)", // Transparent effect
            borderRadius: "4px",
            border: "1px solid rgba(255, 255, 255, 0.2)", // Subtle border
            padding: "5px 26px",
            color: "#ffffff",
            textTransform: "none",
            fontSize: "12px",
            fontWeight: "500",
            display: "flex",
            alignItems: "center",
            gap: "4px",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
          },
        },
        {
          props: { variant: "webbutton" },
          style: {
            background: "#1880DF",
            color: "#ffffff",
            padding: "30px 30px",
            fontSize: "18px",
            borderRadius: "22px",
            fontWeight: 500,
            "&:hover": {
              background: "#1880DF",
              transform: "translateY(-2px)",
              boxShadow: "0 5px 15px rgba(0,0,0,0.2)",
            },
          },
        },
        {
          props: { variant: "offDay" },
          style: {
            backgroundColor: "#1E88E5",
            color: "#ffffff",
            textTransform: "none",
            fontWeight: 600,
            padding: "12px 16px",
            borderRadius: "12px",
            boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
            "&:hover": {
              backgroundColor: "#1565C0",
            },
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontSize: "0.875rem",
          },
        },
        {
          props: { variant: "errorbtn" },
          style: {
            background: "linear-gradient(to top, #FDA1A1, #FF0000)",
            color: "#ffff",
            padding: "30px 30px",
            fontSize: "18px",
            borderRadius: "8px",
            fontWeight: 500,
            "&:hover": {
              background: "linear-gradient(to top, #FDA1A1, #FF0000)",
              transform: "translateY(-2px)",
              boxShadow: "0 5px 15px rgba(0,0,0,0.2)",
            },
          },
        },
      ],
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 8,
          borderRadius: 4,
          backgroundColor: "rgba(255,255,255,0.1)",
        },
        bar: {
          borderRadius: 4,
          background: "linear-gradient(90deg, #1880DF 0%, #1880DF 100%)",
        },
      },
    },
    MuiBox: {
      variants: [
        {
          props: { variant: "authBox" },
          style: {
            backgroundColor: "#f4f4f4",
            padding: "16px",
            borderRadius: "8px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
          },
        },
      ],
    },
    MuiTypography: {
      typography: {
        fontFamily: '"Poppins", sans-serif',
      },
      button: {
        fontFamily: '"Poppins", sans-serif',
      },
      input: {
        fontFamily: '"Poppins", sans-serif',
      },

      styleOverrides: {
        h4: {
          fontWeight: 500,
          color: "#222222",
          fontSize: 25,
        },
        h5: {
          fontWeight: 600,
          color: "#000",
          fontSize: "20px",
        },
        label: {
          color: "#000",
          fontSize: "14px",
          fontWeight: "300",
          mb: 2,
        },
        description: {
          color: "#65758B",
          fontSize: "12px",
          fontWeight: "300",
        },
        root: {
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined", // ✅ Default variant set karo
      },
      variants: [
        {
          props: { variant: "softInput" }, // ✅ Soft Input Variant
          style: {
            backgroundColor: "#eaf3eb", // Light greenish background
            borderRadius: "8px",
            "& .MuiOutlinedInput-root": {
              borderRadius: "15px",
              background: "#eaf3eb",
              "& fieldset": {
                borderColor: "transparent",
                borderRadius: "15px",
              },
              "&:hover fieldset": {
                borderColor: "#fff",
                borderRadius: "15px",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#ffff",
                borderRadius: "15px",
              },
            },
            "& .MuiInputBase-input": {
              padding: "10px 14px",
              borderRadius: "15px",
            },
          },
        },
        {
          props: { variant: "customInput" }, // ✅ Custom Input Variant
          style: {
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            borderRadius: "15px",
            "& .MuiOutlinedInput-root": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              "& fieldset": {
                borderColor: "transparent",
              },
              "&:hover fieldset": {
                borderColor: "#fff",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#fff",
              },
            },
            "& .MuiInputBase-input": {
              color: "white",
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          fontFamily: '"Poppins", sans-serif',
        },
      },
    },
  },
});

export default theme;
